import React, { useCallback, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SHADER_PROVIDER_LABELS } from '@/lib/canvas/shader/authoring/types'

interface ShaderApiKeyFieldProps {
  provider: 'openrouter' | 'grok'
  hasKey: boolean
  /** Saves to this browser's storage (null removes). Never into node state. */
  onSave: (key: string | null) => void
}

/** A pasted provider key: kept in this browser only and sent only to that provider. */
export function ShaderApiKeyField({ provider, hasKey, onSave }: ShaderApiKeyFieldProps) {
  const [draft, setDraft] = useState('')
  const name = SHADER_PROVIDER_LABELS[provider]
  const inputId = `shader-key-${provider}`

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value), [])

  const save = useCallback(() => {
    const key = draft.trim()
    if (!key) return
    onSave(key)
    setDraft('')
  }, [draft, onSave])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()
    if (e.key === 'Enter') save()
  }, [save])

  const remove = useCallback(() => onSave(null), [onSave])

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-xs">{name} API key</label>
      {hasKey ? (
        <div className="flex items-center justify-between rounded-md border border-border px-2 py-1 text-xs">
          <span className="text-muted-foreground">Key saved in this browser</span>
          <Button size="sm" variant="ghost" onClick={remove} className="h-6 px-2 text-[11px]">Remove</Button>
        </div>
      ) : (
        <div className="flex gap-1.5">
          <Input
            id={inputId}
            type="password"
            inputSize="sm"
            autoComplete="off"
            spellCheck={false}
            placeholder={provider === 'grok' ? 'xai-…' : 'sk-or-…'}
            value={draft}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          <Button size="sm" onClick={save} disabled={!draft.trim()} className="h-7 px-2.5 text-[11px]">Save</Button>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground">Stored only in this browser - sent only to {name}.</p>
    </div>
  )
}
